
// Appearance Type Definitions for Helix 2000
// Canonical source of truth for all appearance-related types.
// Used by shared/, game/, server/, and client/ layers.

import type { ResolvedPlayerEquipmentVisuals } from '../equipmentVisuals';
export type { ResolvedPlayerEquipmentVisuals };

export type EyeType = 'round' | 'happy' | 'angry' | 'winking' | 'sleepy' | 'stars';

/**
 * Eye-style library (plan 12 options system). Every style draws two matching
 * eyes except `profile`, the older near-big/far-small look kept as an opt-in.
 * `matched` is the player default: the `eyes` expressions drawn as two equal eyes.
 */
export type EyeStyleId =
  | 'matched' | 'oval' | 'dot' | 'button' | 'wide' | 'sleepy' | 'happy'
  | 'stern' | 'glint' | 'closed' | 'sparkle' | 'lashes' | 'profile';

export interface EyeStyleOptions {
  style: EyeStyleId;
  /** Eye size multiplier, clamped to 0.7–1.4 (default 1). */
  scale?: number;
  /** Extra distance between the eyes in face units, clamped to ±6 (default 0). */
  spacing?: number;
  /** Vertical offset in face units (+ = lower), clamped to ±5 (default 0). */
  height?: number;
}
export type MouthType = 'smile' | 'o' | 'neutral' | 'grin' | 'frown';
export type AccessoryType = 'none' | 'bow' | 'hat' | 'crown' | 'glasses' | 'horns' | 'halo' | 'bandana';
export type ShapeType = 'classic' | 'round' | 'slim' | 'wide';
export type AuraType = 'none' | 'flame' | 'frost' | 'shadow' | 'holy';
export type MarkingType = 'none' | 'scar' | 'tattoo' | 'freckles';
export type TrailType = 'none' | 'sparkles' | 'smoke' | 'petals';

export type WeaponType = 'sword' | 'staff' | 'bow' | 'axe' | 'dagger' | 'mace' | 'wand';
export type ArmorType = 'plate' | 'robe' | 'leather';
export type HelmetType = 'none' | 'hood' | 'helm' | 'bandana';

export interface WeaponConfig {
  type: WeaponType;
  color: string;
  accentColor: string;
  variant: number; // 0=None, 1-3=Styles
}

// Full WoW-style slot system for future-proofing
export interface EquipmentConfig {
  type: ArmorType; // Base armor material (plate/leather/robe) - determines base sprite set
  color: string;
  helmet: HelmetType; // Legacy override, will migrate to 'head' slot

  // The 'Paper Doll' slot system
  slots: {
    head: number;
    neck: number;
    shoulders: number;
    back: number;
    chest: number;
    shirt?: number; // Visual only, under armor
    tabard?: number; // Visual only, over armor
    wrist?: number;
    hands: number; // Gloves
    waist: number;
    legs: number;
    feet: number; // Boots
    finger1?: number;
    finger2?: number;
    trinket1?: number;
    trinket2?: number;

    // Weapons/Held
    mainHand: number;
    offHand: number;
    ranged?: number; // Bow/Gun slot

    // Class Specific
    classItem: number; // Totem, Libram, Quiver, etc.
  }
}

export interface GhostAppearance {
  color: string;
  eyes: EyeType;
  mouth: MouthType;
  shape: ShapeType;
  aura: AuraType;
  faceMarking?: MarkingType;
  blush?: boolean;
  accessory: AccessoryType;
  trail?: TrailType;
  weapon?: WeaponConfig;
  equipment?: EquipmentConfig;
  glow?: boolean;
  bodySize?: 'small' | 'normal' | 'large';
  opacity?: 'solid' | 'ghostly' | 'ethereal';
  actionPose?: boolean;
  /** Renderer-only: render cute dark ghost sockets (no sclera/iris) instead of human sclera. */
  ghostEyes?: boolean;
  /**
   * Renderer-only eye style from the plan-12 library. Absent = the existing
   * `eyes` artwork (cute ghost eyes for art-profile NPCs). Players get
   * `matched` at the render boundary (src/game/rendering/playerEyes.ts); `profile`
   * is the opt-in near-big look. Not replicated yet; a character-creator option later.
   */
  eyeStyle?: EyeStyleOptions;
  /**
   * Renderer-only: draw no feet, so the body's own wavy hem is the bottom edge
   * (lane BC, DJ 2026-10-09 — the player ghost floats, so it has no feet).
   * Boots then have no on-body visual. Players get it at the render boundary
   * (src/game/rendering/playerLook.ts); absent keeps feet for NPCs and presets.
   * Not replicated (toAppearanceMessage lists its keys).
   */
  footless?: boolean;
  /**
   * Renderer-only resolved equipment visuals (BIG-EQUIP-VIS-01 S2).
   *
   * When present, the renderer ignores legacy `weapon` and `equipment` and
   * renders only resolved item descriptors. This is NOT replicated state —
   * it must never appear in Colyseus schemas, persistence, appearance
   * messages, or network conversion. Absence means the legacy NPC/preview
   * appearance path remains active.
   */
  equipmentVisuals?: ResolvedPlayerEquipmentVisuals;
}

/**
 * GhostProps extends GhostAppearance with render-specific configuration.
 * Used by React components (GhostGenerator) and TextureGenerator.
 */
export interface GhostProps extends GhostAppearance {
  size?: number;
  className?: string;
}

// Defaults
export const DEFAULT_GHOST: GhostAppearance = {
  color: '#ffffff',
  eyes: 'round',
  mouth: 'smile',
  shape: 'classic',
  aura: 'none',
  accessory: 'none',
  weapon: { type: 'sword', color: '#C0C0C0', accentColor: '#FFD700', variant: 1 },
  equipment: {
    type: 'leather',
    color: '#555',
    helmet: 'none',
    slots: {
      head: 0, neck: 0, shoulders: 0, back: 0, chest: 0,
      shirt: 0, tabard: 0, wrist: 0, hands: 0, waist: 0,
      legs: 0, feet: 0, finger1: 0, finger2: 0, trinket1: 0,
      trinket2: 0, mainHand: 0, offHand: 0, ranged: 0, classItem: 0
    }
  }
};


